import { useEffect, useState } from 'react';
import { apiRequest } from './client';

// Cab transfers in the API's own format (see /api/transfers in the backend README).

/** Address suggestions for a pickup or drop box: [{ placeId, name }]. */
export const searchPlaces = (query, signal) =>
  apiRequest(`/api/transfers/places?query=${encodeURIComponent(query)}`, { signal });

/** Coordinates for a suggestion, ready to send as a search origin or destination. */
export const getPlace = (placeId, name, signal) =>
  apiRequest(`/api/transfers/places/${encodeURIComponent(placeId)}?name=${encodeURIComponent(name)}`, { signal });

export const searchTransfers = (request, signal) =>
  apiRequest('/api/transfers/search', { body: request, signal });

/** Holds one cab's price. Returns a reviewId for checkout, valid for 30 minutes. */
export const reviewTransfer = (offerId, signal) =>
  apiRequest('/api/transfers/review', { body: { offerId }, signal });

/** Guest or signed-in booking of a reviewed cab; links it to an account by email like flight checkout. */
export const checkoutTransfer = (request, token) =>
  apiRequest('/api/transfers/checkout', { body: request, token });

/** Live status, ride status and tracking link. Guests pass the booking email. */
export const getTransferBooking = (supplierBookingId, email, token, signal) =>
  apiRequest('/api/transfers/bookings/details', {
    body: { supplierBookingId, email: email || null },
    token,
    signal,
  });

/** Refund for cancelling now. Requires sign-in. */
export const quoteTransferCancellation = (supplierBookingId, token) =>
  apiRequest('/api/transfers/bookings/cancellation/quote', { body: { supplierBookingId }, token });

export const cancelTransfer = (supplierBookingId, cancellationId, token) =>
  apiRequest('/api/transfers/bookings/cancellation', { body: { supplierBookingId, cancellationId }, token });

// Resolved places, so moving between results and details doesn't look them up again.
const placeCache = new Map();

/**
 * A pickup or drop point from the URL: by place id when the customer picked a suggestion,
 * otherwise (popular-route links, older links) the best match for the name.
 */
export async function resolvePlace({ name, placeId }, signal) {
  const key = placeId || `name:${name.toLowerCase()}`;
  if (placeCache.has(key)) return placeCache.get(key);
  let id = placeId;
  let label = name;
  if (!id) {
    const [best] = await searchPlaces(name, signal);
    if (!best) throw new Error(`We couldn't find "${name}". Choose the address from the suggestions.`);
    id = best.placeId;
    label = best.name;
  }
  const place = await getPlace(id, label, signal);
  placeCache.set(key, place);
  return place;
}

/**
 * Resolves the pickup and drop points, then searches whenever the search changes; `retry` searches again.
 * `toRequest(origin, destination)` builds the API request once both places are known.
 */
export function useTransferSearch(search, toRequest) {
  const key = search ? JSON.stringify(search) : '';
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: Boolean(search), data: null, error: '' });

  useEffect(() => {
    if (!key) return undefined;
    const s = JSON.parse(key);
    const controller = new AbortController();
    setState({ loading: true, data: null, error: '' });
    Promise.all([resolvePlace(s.pickup, controller.signal), resolvePlace(s.drop, controller.signal)])
      .then(([origin, destination]) => searchTransfers(toRequest(s, origin, destination), controller.signal))
      .then((data) => setState({ loading: false, data, error: '' }))
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({ loading: false, data: null, error: error.message || 'We could not load cabs for this trip.' });
      });
    return () => controller.abort();
    // toRequest is a pure function of the search, which is already in the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  return { ...state, retry: () => setAttempt((n) => n + 1) };
}
